import type { AppLayerSourceType, TileLayerRow } from './types';

/** Synthetic TOC section key for project-scoped Site Data overlays. */
export const SITE_DATA_TOC_PARENT_KEY = '_toc:Site Data' as const;

/**
 * Engineering-owned TOC parent for Site Data (not in the GIS CSV). App rows
 * `fieldNotes` / `potholes` hang off this key; `deriveTocTree` only treats
 * `origin:'gis' + category:'toc'` rows as parents — injected at read time.
 */
export const SITE_DATA_TOC_PARENT: TileLayerRow = {
  origin: 'gis',
  key: SITE_DATA_TOC_PARENT_KEY,
  parentKey: null,
  label: 'Site Data',
  category: 'toc',
  sourceUrl: null,
  sortOrder: null,
  enabled: true,
  config: {},
  downloadName: null,
  specialDatasetSlug: null,
};

/** Shape for hand-maintained `appLayers` seed rows (fixture + DB seed). */
export interface AppLayerRowSeed {
  key: string;
  parentKey: string | null;
  label: string;
  sourceType: AppLayerSourceType;
  sourceUrl: string | null;
  sortOrder: number | null;
  enabled: boolean;
  isDigitized: boolean;
  isDownloadable: boolean;
  downloadName: string | null;
  config: Record<string, unknown>;
}

/** Site Data leaves seeded into `appLayers` and mirrored in the fixture. */
export const SITE_DATA_APP_LAYER_SEEDS: AppLayerRowSeed[] = [
  {
    key: 'fieldNotes',
    parentKey: SITE_DATA_TOC_PARENT_KEY,
    label: 'Field Notes',
    sourceType: 'client_geojson',
    sourceUrl: null,
    sortOrder: 1,
    enabled: true,
    isDigitized: false,
    isDownloadable: false,
    downloadName: null,
    config: {},
  },
  {
    key: 'potholes',
    parentKey: SITE_DATA_TOC_PARENT_KEY,
    label: 'Potholes',
    sourceType: 'client_geojson',
    sourceUrl: null,
    sortOrder: 2,
    enabled: true,
    isDigitized: false,
    isDownloadable: false,
    downloadName: null,
    config: {},
  },
];

/** Prepend the Site Data TOC parent when absent (idempotent). */
export function injectSiteDataTocParent(gisRows: TileLayerRow[]): TileLayerRow[] {
  if (gisRows.some((row) => row.key === SITE_DATA_TOC_PARENT_KEY)) return gisRows;
  return [SITE_DATA_TOC_PARENT, ...gisRows];
}
